import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { jaJP } from '@mui/x-data-grid';
import { usePost } from './../../apis/ApiRequests';
import { useAuth } from '../../contexts/AuthContext';
import { dateToString } from '../../utils/Library';
import Header from '../../components/Header';
import Frame from '../../components/uiParts/Frame';
import TitleBar from '../../components/uiParts/TitleBar';
import { CustomDataGrid } from '../../components/dataGrid/CustomDataGrid';
import BidSearchForm from '../../components/uiParts/bid/BidSearchForm';
import CustomSnackBar from '../../components/alert/CustomSnackBar';
import CustomConfirm from '../../components/confirm/CustomConfirm';

const DeliveryList = () => {

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - セッション情報を取得
	//-----------------------------------------------
    const { authInfo }   = useAuth();
    const facilityCode   = authInfo.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId;
    const luserName      = authInfo.userName;
    const authority      = authInfo.authority;
    const today          = authInfo.targetDate;
    const navigate       = useNavigate();
    
    const search    = useLocation().search;
    const query     = new URLSearchParams(search);
    const targetDate  = query.get("targetDate") || today;
    const checkStatus  = query.get("checkStatus") || 1;

	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ entryDate,             setEntryDate             ] = useState(targetDate);
    const [ condition ,            setCondition             ] = useState('');
    const [ searchStatus ,         setSearchStatus          ] = useState(checkStatus);
    const [ searchCount,           setSearchCount           ] = useState(0);
    const [ dataGridHeight,        setDataGridHeight        ] = useState(window.innerHeight - 210);
    const [ dataInputList,         setDataInputList         ] = useState([]);
    const [ checkedDataInputList,  setCheckedDataInputList  ] = useState([]);
    
    // UI Message
    const [ messageState, setMessageState ] = useState({
        snackBarTrue        : false,
        snackBarTrue_print  : false,
        snackBarFalse       : false,
        require             : false,
        confirm             : false,
        confirm_next        : false,
        confirm_delete_row  : false,
        confirm_register    : false,
        confirm_status_done : false,
        confirm_status_none : false
    });
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
	// API - searchData / BidGroup
	//-----------------------------------------------
	const { response : searchData_res, loading : searchData_Loading, post:searchData_post } = usePost();
	const searchData = async () => {
        const numericDate = dateToString(entryDate);
        const postData = {
            'className'    : 'mch.models.table.BidGroup',
            'methodName'   : 'searchDataByDelivery',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'keyword1'     : numericDate,
            'keyword2'     : condition,
            'keyword3'     : searchStatus
        }
        await searchData_post(postData);
	};
	//-----------------------------------------------
    // API - updateTransaction / Transaction
	//-----------------------------------------------
    const { response : updateData_res, loading : updateData_Loading, post:updateData_post } = usePost();
    const updateData = async (targetDate, checkStatus) => {
        const postData = {
            'className'     : 'mch.models.table.BidHistory',
            'methodName'    : 'updateDeliveryFlag',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'bidGroupNo'    : targetDate.bidGroupNo,
            'traderCode'    : targetDate.traderCode,
            'seq'           : targetDate.seq,
            'deliveryStatus': checkStatus
        }
        await updateData_post(postData);
    };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        setSearchCount(searchCount+1);
        
        const handleResize = () => {
            setDataGridHeight(window.innerHeight - 210);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    },[]);
	//-----------------------------------------------
    // useEffect - 検索処理
	//-----------------------------------------------
    useEffect(() => {
        searchData();
    }, [searchCount]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(searchDataByDataInput / bidGroup)
	//-----------------------------------------------
    useEffect(() => {
        setDataInputList([]);
        if (searchData_res) {
            setDataInputList(searchData_res.Data);
        }
    }, [searchData_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(updateData / bidList)
	//-----------------------------------------------
    useEffect(() => {
        if (updateData_res) {
            setMessageInfo('confirm_status_done', false);
            setMessageInfo('confirm_status_none', false);
            setMessageInfo('snackBarTrue', true);
            setSearchCount(searchCount+1);
        }
    }, [updateData_res]);
    useEffect(() => {
        if (dataInputList) {
            // bidStatusが1の行のIDを集める
            const selectedRows = dataInputList.filter(row => row.deliveryStatus === 1).map(row => row.id);
            setCheckedDataInputList(selectedRows);
        }
    }, [dataInputList]);

	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 日付の変更
	//-----------------------------------------------
    const handleChange_entryDate = (value) => {
        setEntryDate(value);
        setSearchCount(searchCount+1);
    };
	//-----------------------------------------------
    // eventHandler - 検索ボタン
	//-----------------------------------------------
    const handleClick_search = () => {
        setSearchCount(searchCount+1);
    };
	//-----------------------------------------------
    // eventHandler - 検索クリアボタン
	//-----------------------------------------------
    const handleClick_search_clear = () => {
        setEntryDate(today);
        setCondition('');
        setSearchStatus(1);
        setSearchCount(searchCount+1);
    };
	//===============================================
	// dataGrid
	//===============================================
	//-----------------------------------------------
    // dataGrid - リストの見出し
	//-----------------------------------------------
    const columnsxBidGroup = [
        { field: 'entryNo', headerName: '入札No', width: 80, columnSeparatorWidth: 1, sortable:false, },
        { field: 'seq', headerName: '連番', width: 80, columnSeparatorWidth: 1, sortable:false, },
        { field: 'bidName', headerName: '品名', width: 290, columnSeparatorWidth: 1, sortable:false, },
        { field: 'trader', headerName: '仲買人', width: 120, columnSeparatorWidth: 1, sortable:false, },
        { field: 'tanka', headerName: '単価', width: 80, columnSeparatorWidth: 1, type:'number', sortable:false, editable:false},
        { field: 'bidSuryo', headerName: '数量', width: 80, columnSeparatorWidth: 1, type:'number', sortable:false, editable:false},
        {
            field: 'kgKubun',
            headerName: '単位',
            width: 80, sortable:false,
            renderCell: (params) => (
              <div>
                {unitMapping[params.value] || params.value}
              </div>
            ),
        },
    ];
    const unitMapping = {
        1: 'Kg',
        0: '箱',
        2: '本',
        3: '匹',
        4: '袋',
    };
    //-----------------------------------------------
    // dataGrid - 行の更新処理
    //-----------------------------------------------
    const handleProcessRowUpdate = (newRow, oldRow) => {
        let targetId = newRow.id;
        setDataInputList((prevData) => {
            return prevData.map((row) => {
                if (row.id === targetId) {
                    return newRow;
                }
                return row;
            });
        });
        return newRow;
    };
    //-----------------------------------------------
    // dataGrid - 行の選択
    //-----------------------------------------------
    const handleClick_dataInputList = (event) => {
        if (event.field === '__check__') {
            if (checkedDataInputList.includes(event.id)) {
                setCheckedDataInputList(checkedDataInputList.filter((checkedDataInputList, index) => (checkedDataInputList !== event.id)));
            } else {
                setCheckedDataInputList((checkedDataInputList)=>[...checkedDataInputList, event.id]);
            }
        }
    };
	//===============================================
	// Function
	//===============================================
	//-----------------------------------------------
    // Function - 登録処理
	//-----------------------------------------------
    const register = (status) => {

        dataInputList.map((targetData) => {
            targetData.activeFlag  = 1;
            const isChecked = checkedDataInputList.includes(targetData.id);;
            if (isChecked) {
                status = 1;
            } else {
                status = 0;
            }
            updateData(targetData, status);
        });
        return false;
    };
	//===============================================
	// UI Message
	//===============================================
    const setMessageInfo = (target, state) => {
        setMessageState(prevSearchData => ({
            ...prevSearchData,
            [target]: state,
        }));
    };
    //===============================================
    // Css
    //===============================================
    
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/home" setEntryDate={handleChange_entryDate} />
            <div className='dataInput bodyContens'>
                <TitleBar title="荷渡し" />
                <div className='mainContents'>
                    <Frame classname="frame" title="入札検索">
                        <div style={{display:'flex'}}>
                            <div> 
                                <BidSearchForm
                                    targetDateId       = "dataInput_targetDate"
                                    targetDateValue    = { entryDate }
                                    targetDateOnChange = { (e) => setEntryDate(e.target.value) }
                                    keywordId          = "dataInput_condition"
                                    keywordValue       = { condition }
                                    keywordOnChange    = { (e) => setCondition(e.target.value) }
                                    handleSearch       = { () => handleClick_search() }
                                    handleSearchClear  = { () => handleClick_search_clear() }
                                />
                            </div>
                            <div style={{marginLeft:'10px'}}>
                                <Button
                                    color     = "primary"
                                    style     = {{color:'#fff'}}
                                    size      = "midium"
                                    variant   = "contained"
                                    startIcon = {<CheckIcon />}
                                    onClick   = { () => setMessageInfo('confirm_status_done', true) }
                                >
                                荷渡し完了
                                </Button>
                            </div>
                        </div>
                        <div style={{marginTop:'10px'}}>
                            <CustomDataGrid
                                getRowClassName={(params) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                }
                                editMode                = "row"
                                disableSelectionOnClick = { true } 
                                rows                    = { dataInputList }
                                columns                 = { columnsxBidGroup }
                                sx                      = { {height:dataGridHeight,fontSize:14,borderColor: 'primary.light',} }
                                density                 = "compact"
                                localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                                showColumnRightBorder
                                showCellRightBorder
                                checkboxSelection
                                disableRowSelectionOnClick = {false}
                                disableColumnMenu          = {true}
                                onCellClick                = {(event) => handleClick_dataInputList(event)}
                                processRowUpdate           = {handleProcessRowUpdate}
                                onRowSelectionModelChange  = {(newSelectionModel) => { setCheckedDataInputList(newSelectionModel) }}
                                rowSelectionModel          = {checkedDataInputList}
                                loading = { searchData_Loading }

                            />
                        </div>
                    </Frame>
                </div>
            </div>
                <CustomSnackBar
                    open            = {messageState.snackBarTrue}
                    onClose         = {() => setMessageInfo('snackBarTrue', false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "登録しました"
                />
                <CustomSnackBar
                    open            = {messageState.snackBarTrue_print}
                    onClose         = {() => setMessageInfo('snackBarTrue_print', false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "印刷指示を送信しました"
                />
                <CustomSnackBar
                    open            = {messageState.snackBarFalse}
                    onClose         = {() => setMessageInfo('snackBarFalse', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = "登録に失敗しました"
                />
                <CustomConfirm
                    open      = {messageState.confirm_status_done}
                    onClose   = {() => setMessageInfo('confirm_status_done', false)}
                    onClickNG = {() => setMessageInfo('confirm_status_done', false)}
                    onClickOK = {() => register(1)}
                    message   = {'ステータスをチェック完了にします。よろしいですか？'}
                />
                <CustomConfirm
                    open      = {messageState.confirm_status_none}
                    onClose   = {() => setMessageInfo('confirm_status_none', false)}
                    onClickNG = {() => setMessageInfo('confirm_status_none', false)}
                    onClickOK = {() => register(0)}
                    message   = {'ステータスをチェック未完了にします。よろしいですか？'}
                />
        </>
    )
}
export default DeliveryList;